import { useMemo, useState } from 'react';

import clsx from 'clsx';
import { useDebounce } from 'use-debounce';
import { Controller, useFormContext } from 'react-hook-form';

import { Combobox } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';

import { getByPath } from '../../../utils/getByPatth';
import { getErrorTypeText } from './getErrorTypeText';
import ThemeColorOptions from '../../../types/system/ThemeColorOption';

interface SelectionPillProps extends AutoCompleteComboboxOption {
  onRemoveClick: (option: AutoCompleteComboboxOption) => void;
}

function SelectionPill({ onRemoveClick, ...opt }: SelectionPillProps) {
  return (
    <span className="rounded-full inline-flex items-center py-0.5 pl-3 pr-1 text-sm space-x-1 leading-4 bg-gray-200 dark:bg-gray-700">
      <span>{opt.label}</span>
      <XMarkIcon
        onClick={() => onRemoveClick(opt)}
        className="rounded-full h-6 w-6 p-0.5 bg-gray-400 dark:bg-gray-900 hover:cursor-pointer"
      />
    </span>
  );
}

export interface AutoCompleteComboboxOption {
  value: string;
  label: string;
}

interface AutoCompleteComboboxProps {
  name: string;
  options: AutoCompleteComboboxOption[];
  small?: boolean;
  color?: ThemeColorOptions;
  label?: string;
}

export function FormAutoCompleteCombobox({
  name,
  options,
  small = false,
  color = 'primary',
  label,
}: AutoCompleteComboboxProps) {
  const [query, setQuery] = useState('');
  const [debouncedQuery] = useDebounce(query, 300);
  const {
    control,
    setValue,
    formState: { errors },
  } = useFormContext();

  const errorKey = name as string;
  const extractedError = getByPath(errorKey, errors);

  const getErrorText = () => {
    if (extractedError) {
      if (extractedError?.message) {
        return extractedError?.message as string;
      } else {
        return `${label} is ${getErrorTypeText(
          extractedError?.type as string
        )}`;
      }
    } else {
      return `${label} is ${getErrorTypeText(
        errors[errorKey]?.type as string
      )}`;
    }
  };

  const opts = useMemo(
    () =>
      debouncedQuery === ''
        ? options
        : options.filter((opt) =>
            opt.label.toLowerCase().includes(debouncedQuery.toLowerCase())
          ),
    [debouncedQuery, options]
  );

  return (
    <Controller
      control={control}
      name={name}
      render={({ field: { value = [], onChange } }) => {
        const handleRemove = (removeOpt: AutoCompleteComboboxOption) => {
          const newValue = value.filter(
            (opt: AutoCompleteComboboxOption) => opt.value !== removeOpt.value
          );
          setValue(name, newValue);
          onChange(newValue);
        };

        return (
          <div className="flex flex-col space-y-1 overflow-visible">
            {label && (
              <label className="text-sm font-medium opacity-80">{label}</label>
            )}
            {value.length > 0 && (
              <div className="block space-x-2">
                {value.map((opt: AutoCompleteComboboxOption) => (
                  <SelectionPill
                    key={opt.value}
                    {...opt}
                    onRemoveClick={handleRemove}
                  />
                ))}
              </div>
            )}
            <Combobox
              as="div"
              by={(a, b) => a.value === b.value}
              value={value}
              onChange={(selected) => {
                setQuery('');
                setValue(name, selected);
                onChange(selected);
              }}
              multiple
            >
              <div className="relative overflow-visible">
                <Combobox.Input
                  placeholder="Search..."
                  value={query}
                  onChange={(e) => setQuery(e.currentTarget.value)}
                  className={clsx(
                    'w-full rounded bg-white border ring-0 focus:ring-2 outline-0 focus:outline-0 dark:bg-gray-700 dark:text-white',
                    color === 'primary'
                      ? '!border-primary !ring-primary'
                      : '!border-secondary !ring-secondary',
                    small ? 'px-1 py-0.5 pr-10' : 'h-[42px] px-3 py-2 pr-10'
                  )}
                />
                <Combobox.Options
                  className={clsx(
                    'absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded bg-white py-1 text-base shadow-lg ring-1 focus:outline-none sm:text-sm dark:bg-gray-700 dark:text-white',
                    color === 'primary' ? '!ring-primary' : '!ring-secondary'
                  )}
                >
                  {opts.length > 0 ? (
                    opts.map((opt) => (
                      <Combobox.Option key={opt.value} value={opt}>
                        {({ active, selected }) => (
                          <li
                            className={clsx(
                              'group relative cursor-default select-none py-2 pl-3 pr-9',
                              selected && 'bg-primary text-primaryContrast',
                              active && 'bg-primary/80 text-primaryContrast'
                            )}
                          >
                            {opt.label}
                          </li>
                        )}
                      </Combobox.Option>
                    ))
                  ) : (
                    <Combobox.Option
                      className="group relative cursor-default select-none opacity-80 italic text-center py-2 pl-3 pr-9"
                      disabled
                      value={{}}
                    >
                      {debouncedQuery === '' ? 'No Options' : 'No Results'}
                    </Combobox.Option>
                  )}
                </Combobox.Options>
              </div>
            </Combobox>
            {extractedError && (
              <p className="text-xs text-red-500">{getErrorText()}</p>
            )}
          </div>
        );
      }}
    />
  );
}
